import { compact, includes, isEmpty, noop, omit, uniqBy } from "lodash";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { DescribeFindingResponse } from "types/api/describeFindingResponse";
import { FindingSummary } from "types/api/findingSummary";
import { FilterInfo } from "types/pagination";

type FindingType = FindingSummary | DescribeFindingResponse;

export type FindingBucketContextType = {
  findingIds: number[];
  findings: FindingType[];
  addFilter: (key: string, filters: FilterInfo, count: number) => void;
  filterMap: { global?: FilterInfo; [key: string]: FilterInfo | undefined };
  totalCountMap: { [key: string]: number };
  addFindings: (ids: number[], findings: FindingType[]) => void;
  removeFinding: (id: number) => void;
  clear: () => void;
  submit: (
    data: object,
    api: (data: object) => { promise: Promise<unknown>; cancel: () => void },
  ) => Promise<unknown>;
  totalCount: number;
  findingFilterGroups: () => Array<any>;
};

const FindingBucketContext = React.createContext<FindingBucketContextType>({
  findingIds: [],
  findings: [],
  addFilter: noop,
  filterMap: {},
  totalCountMap: {},
  addFindings: noop,
  removeFinding: noop,
  clear: noop,
  submit: (...args) => Promise.resolve(),
  totalCount: 0,
  findingFilterGroups: () => [],
});

export function FindingBucketProvider({
  children,
}: {
  children: JSX.Element | JSX.Element[];
}) {
  const [totalCountMap, setTotalCountMap] = useState<{ [key: string]: number }>(
    {},
  );
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  const [selectedFindings, setSelectedFindings] = useState<FindingType[]>([]);

  const [selectedFilters, setSelectedFilters] = useState<
    FindingBucketContextType["filterMap"]
  >({});

  const addFilter = useCallback<FindingBucketContextType["addFilter"]>(
    (key, filters, count) => {
      setSelectedFilters((prev) => {
        if (isEmpty(filters)) {
          return omit(prev, key);
        }
        return { ...prev, [key]: filters };
      });
      setTotalCountMap((prev) => {
        if (isEmpty(filters)) {
          return omit(prev, key);
        }
        return { ...prev, [key]: count };
      });
    },
    [],
  );

  const addFindings = useCallback<FindingBucketContextType["addFindings"]>(
    (ids, findings) => {
      setSelectedIds(ids || []);
      setSelectedFindings((prev) => {
        // previously selected findings aren't available with the table, so we need to merge
        // mapping the finding list with ids list to make sure no unwanted findings are stored
        return uniqBy(compact([...prev, ...findings]), "id").filter((finding) =>
          includes(ids, finding.id),
        );
      });
    },
    [],
  );
  const removeFinding = useCallback<FindingBucketContextType["removeFinding"]>(
    (id) => {
      setSelectedIds((prev) => {
        const _newIds = [...prev];
        if (_newIds.includes(id)) {
          _newIds.splice(_newIds.indexOf(id), 1);
        }
        return _newIds;
      });
      setSelectedFindings((prev) => {
        return prev.filter((f) => f.id !== id);
      });
    },
    [],
  );
  const clear = useCallback(() => {
    setSelectedIds([]);
    setSelectedFindings([]);
    setSelectedFilters({});
    setTotalCountMap({});
  }, []);

  const findingFilterGroups = useCallback<
    FindingBucketContextType["findingFilterGroups"]
  >(() => {
    let filters: FilterInfo[] = [];
    const filterGroups: any[] = [];
    if (!isEmpty(selectedFilters)) {
      filters = compact(Object.values(selectedFilters));
    } else if (selectedIds.length) {
      filters = [
        {
          id_term: [...selectedIds],
        },
      ];
    }

    if (filters.length) {
      filters.forEach((filter) => {
        const payloadFilter = {
          filters: filter,
        };
        filterGroups.push(payloadFilter);
      });
    }
    return filterGroups;
  }, [selectedFilters, selectedIds]);

  const submit = useCallback<FindingBucketContextType["submit"]>(
    (data, api) => {
      let filters: FilterInfo[] = [];
      if (!isEmpty(selectedFilters)) {
        filters = compact(Object.values(selectedFilters));
      } else if (selectedIds.length) {
        filters = [
          {
            id_term: [...selectedIds],
          },
        ];
      }
      if (filters.length) {
        const promises: Promise<unknown>[] = [];
        filters.forEach((filter) => {
          const payload = {
            ...data,
            findingFilterRequest: { filters: filter },
          };
          promises.push(api(payload).promise);
        });
        return Promise.all(promises);
      }
      return Promise.resolve();
    },
    [selectedFilters, selectedIds],
  );

  const totalCount = isEmpty(selectedIds)
    ? Object.values(totalCountMap).reduce((agg, current) => agg + current, 0)
    : selectedIds.length;

  const value = useMemo<FindingBucketContextType>(() => {
    return {
      findingIds: selectedIds,
      findings: selectedFindings,
      addFilter,
      filterMap: selectedFilters,
      addFindings,
      removeFinding,
      clear,
      submit,
      totalCount,
      totalCountMap,
      findingFilterGroups,
    };
  }, [
    selectedIds,
    addFilter,
    selectedFilters,
    addFindings,
    selectedFindings,
    removeFinding,
    clear,
    submit,
    totalCount,
    totalCountMap,
    findingFilterGroups,
  ]);

  return (
    <FindingBucketContext.Provider value={value}>
      {children}
    </FindingBucketContext.Provider>
  );
}

export const useFindingBucket = () => {
  return useContext(FindingBucketContext);
};
